import { Controller } from "@hotwired/stimulus"

// Goal creation on a single screen: the card at the top is a live preview of
// the goal being built, and every control below writes straight into it.
//
// Three inputs are display-only and mirror into hidden fields, so the form
// submits plain values: the amount (shown with thousands separators), the
// icon index (picked from the dock) and the deadline (a stamp or the native
// date picker).
const TICK_COUNT = 140
// Currency units per tick of ribbon travel. Two pixels of drag move one step.
const RIBBON_STEP = 25
// Resting level of the preview track. Matches the goal screen's own floor
// (goals/_hero), so an untouched form and an empty goal read the same.
const EMPTY_FILL = 4

export default class extends Controller {
  static targets = [
    "fill", "previewIcon", "previewName", "previewMeta", "previewAmount",
    "previewAmountValue", "rate", "rateValue",
    "nameField", "suggestion", "suggestions",
    "dock", "icon", "emojiField",
    "amountDisplay", "amountSizer", "amountField", "amountChip", "ribbon", "ticks",
    "dateField", "dateInput", "dateStampLabel", "stamp",
    "foldIcon", "foldAmount", "foldDate",
    "submit"
  ]

  static values = {
    locale: String,
    untitled: String,
    noDeadline: String,
    targetOn: String,
    symbol: String,
    symbolFirst: Boolean,
    monthUnit: String,
    monthsUnit: String,
    defaultMonths: Number,
    // An edit opens on a goal that is already saved, so its Save has nothing to
    // do until something differs. A new goal has no such baseline: everything
    // about it is a change.
    editing: Boolean
  }

  connect() {
    this.amount = parseInt(this.amountFieldTarget.value, 10) || 0
    this.months = null
    this.dragging = false
    this.pickDateLabel = this.dateStampLabelTarget.textContent

    this.#buildTicks()
    this.#centreIcon()
    this.#fitAmount()
    this.#refitOnFontLoad()
    this.#openingTerm()
    this.render()
    // Taken after the opening render, not before it: connect settles the
    // deadline and normalises the amount, and a baseline read any earlier would
    // count the form's own setup as the user's first edit.
    this.baseline = this.#state()
  }

  // ── Folds ──

  // Icon and amount both measure themselves on open, and a folded section has
  // no width to measure — the dock centres on nothing and the amount field
  // sizes itself to zero. Both are re-run the moment their fold has a box.
  foldToggled(event) {
    const fold = event.target
    if (!fold.open) return

    if (fold.dataset.fold === "icon") this.#centreIcon()
    if (fold.dataset.fold === "amount") this.#fitAmount()
  }

  // ── Name ──

  typeName() {
    this.#markSuggestion()
    this.render()
  }

  showSuggestions() {
    this.suggestionsTarget.dataset.on = "true"
  }

  hideSuggestions() {
    this.suggestionsTarget.dataset.on = "false"
  }

  // Keeps the field focused through a chip's pointerdown. Without it the blur
  // collapses the rail before the chip's own click can land on it.
  keepFocus(event) {
    event.preventDefault()
  }

  // A suggestion sets the name and dresses the goal in the icon that goes
  // with it, so one tap finishes two sections.
  pickSuggestion(event) {
    const chip = event.currentTarget

    this.nameFieldTarget.value = chip.dataset.name
    this.#selectIcon(chip.dataset.icon)
    this.#centreIcon(true)
    this.#markSuggestion()
    // The rail did its job the moment it filled the field.
    this.hideSuggestions()
    this.render()
  }

  // ── Icon ──

  pickIcon(event) {
    const button = event.currentTarget

    this.#selectIcon(button.dataset.icon)
    this.#centreInDock(button, true)
  }

  // ── Amount ──

  focusAmount() {
    this.amountDisplayTarget.focus()
  }

  // A preset chip is a shortcut to a round number. It goes through the same
  // door as the dial, so the ruler and the vessel follow it.
  pickAmount(event) {
    this.#setAmount(parseInt(event.currentTarget.dataset.amount, 10))
  }

  typeAmount(event) {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 9)

    this.amount = digits ? parseInt(digits, 10) : 0
    event.target.value = digits ? this.#withSeparators(this.amount) : ""
    this.#fitAmount()
    this.#shiftTicks()
    this.render()
  }

  ribbonStart(event) {
    this.dragging = true
    this.dragOrigin = event.clientX
    this.dragBase = this.amount
    this.ribbonTarget.setPointerCapture(event.pointerId)
  }

  ribbonMove(event) {
    if (!this.dragging) return

    this.#setAmount(this.dragBase - (event.clientX - this.dragOrigin) * RIBBON_STEP / 2)
  }

  ribbonEnd() {
    this.dragging = false
  }

  // ── Deadline ──

  pickDate(event) {
    this.#selectTerm(event.currentTarget)
    this.render()
  }

  // A hand-picked date wins over the stamps, and the stamp wears the date.
  syncDate() {
    const value = this.dateInputTarget.value

    this.dateFieldTarget.value = value

    if (value) {
      const days = (new Date(value) - new Date()) / 86400000
      this.months = Math.max(1, Math.round(days / 30))
      this.dateStampLabelTarget.textContent = this.#shortDate(value)
      this.#markStamp(this.dateInputTarget.closest("[data-goal-form-target~='stamp']"))
    } else {
      this.months = null
      this.dateStampLabelTarget.textContent = this.pickDateLabel
      this.#markStamp(null)
    }

    this.render()
  }

  // ── Render ──

  render() {
    const name = this.nameFieldTarget.value.trim()

    this.previewNameTarget.textContent = name || this.untitledValue
    this.previewNameTarget.classList.toggle("text-base-content/30", !name)

    this.previewAmountValueTarget.textContent = this.amount ? this.#withSeparators(this.amount) : "0"
    this.previewAmountTarget.classList.toggle("text-base-content/20", !this.amount)
    // The preview's progress is a track now, so the level runs across rather
    // than up. Same figure, different axis. Creation drops the track entirely —
    // there is no progress on a goal that does not exist yet — so the level is
    // only written where a track was rendered.
    if (this.hasFillTarget) this.fillTarget.style.width = `${this.#fillPercent()}%`

    this.previewMetaTarget.textContent = this.#metaLabel()
    this.#renderRate()

    this.#markAmountChip()
    this.#renderFoldAnswers()

    this.amountFieldTarget.value = this.amount || ""
    // Nothing to save without a name, so the nav-bar checkmark stays greyed —
    // and on an edit, nothing to save until something differs from what the
    // goal already holds. bridge--button greys the native bar item in step.
    this.submitTarget.disabled = name.length === 0 || (this.editingValue && !this.#touched())
  }

  // ── Internals ──

  // Everything the form can send. The icon, amount and deadline all live in
  // hidden fields written by this controller rather than typed into, so no
  // input event announces them — reading the fields themselves is what catches
  // an icon swap or a term stamp the way it catches a keystroke.
  #state() {
    return [
      this.nameFieldTarget.value.trim(),
      this.emojiFieldTarget.value,
      this.amountFieldTarget.value,
      this.dateFieldTarget.value
    ].join(" ")
  }

  // Undoing a change greys Save again: the form is back to the goal on record,
  // and a live Save there would write a row identical to the one it read.
  #touched() {
    return this.baseline !== undefined && this.baseline !== this.#state()
  }

  // The term the form opens on. A date already on the record wins — that is a
  // rejected submission coming back, and the user's own pick outranks the
  // default the form ships with.
  #openingTerm() {
    if (this.dateFieldTarget.value) {
      this.dateInputTarget.value = this.dateFieldTarget.value
      this.syncDate()
      return
    }

    const stamp = this.stampTargets.find((s) => s.dataset.months === String(this.defaultMonthsValue))
    if (stamp) this.#selectTerm(stamp)
  }

  // Lighting a stamp and writing the date it stands for. Shared by the opening
  // term and by a tap, so both leave the form in the same state.
  #selectTerm(stamp) {
    this.months = parseInt(stamp.dataset.months, 10)
    this.dateFieldTarget.value = this.#dateInMonths(this.months)
    this.dateInputTarget.value = ""
    this.dateStampLabelTarget.textContent = this.pickDateLabel
    this.#markStamp(stamp)
  }

  #setAmount(value) {
    this.amount = Math.max(0, Math.round(value / RIBBON_STEP) * RIBBON_STEP)
    this.amountDisplayTarget.value = this.amount ? this.#withSeparators(this.amount) : ""
    this.#fitAmount()
    this.#shiftTicks()
    this.render()
  }

  // The field is widened to its own text so the pencil sits right beside the
  // number instead of floating at the end of a fixed-width box. `size` is no
  // good here: it counts average glyph widths, which under-measures badly at
  // 42px and clips the number.
  #fitAmount() {
    const input = this.amountDisplayTarget

    this.amountSizerTarget.textContent = input.value || input.placeholder
    // getBoundingClientRect keeps the sub-pixel width; offsetWidth rounds to a
    // whole pixel and the last glyph loses whatever fraction it was owed.
    const width = this.amountSizerTarget.getBoundingClientRect().width
    // Inside a shut fold the sizer has no box, and a zero measurement would
    // pin the field at 6px for good. foldToggled measures again on open.
    if (!width) return

    input.style.width = `${Math.ceil(width) + 6}px`
  }

  // Nunito Sans is a swap-loaded variable font, so the first measurement can
  // land while the fallback face is still showing. Nunito at weight 900 is the
  // wider of the two, which leaves the field too narrow and clips the number.
  // Measuring again once the real face is in settles it.
  #refitOnFontLoad() {
    document.fonts?.ready.then(() => {
      if (this.element.isConnected) this.#fitAmount()
    })
  }

  // The dock already holds a correctly fingerprinted <img> per icon, so the
  // preview copies its src rather than rebuilding an asset path by hand.
  #selectIcon(index) {
    this.emojiFieldTarget.value = index
    this.iconTargets.forEach((icon) => { icon.dataset.on = String(icon.dataset.icon === index) })

    const source = this.iconTargets.find((icon) => icon.dataset.icon === index)?.querySelector("img")
    if (!source) return

    // The card and the fold's own answer both wear the chosen icon.
    for (const holder of [ this.previewIconTarget, this.foldIconTarget ]) {
      const img = holder.querySelector("img")
      if (img) img.src = source.src
    }
  }

  // A chip stays lit only while the field still reads exactly as it typed it.
  #markSuggestion() {
    const name = this.nameFieldTarget.value.trim()

    this.suggestionTargets.forEach((chip) => {
      chip.dataset.on = String(chip.dataset.name === name)
    })
  }

  // A chip stays lit only while the dial still reads exactly its amount.
  #markAmountChip() {
    this.amountChipTargets.forEach((chip) => {
      chip.dataset.on = String(parseInt(chip.dataset.amount, 10) === this.amount)
    })
  }

  #markStamp(active) {
    this.stampTargets.forEach((stamp) => {
      stamp.dataset.on = String(stamp === active)
    })
  }

  #buildTicks() {
    const ticks = document.createDocumentFragment()

    for (let i = 0; i < TICK_COUNT; i++) {
      const tick = document.createElement("span")
      if (i % 5 === 0) tick.className = "is-major"
      ticks.appendChild(tick)
    }

    this.ticksTarget.appendChild(ticks)
    this.#shiftTicks()
  }

  // The ruler slides under a fixed needle. Ticks repeat every 100px, so the
  // offset wraps and a handful of ticks covers any amount.
  #shiftTicks() {
    this.ticksTarget.style.transform = `translateX(${-(this.amount / RIBBON_STEP * 10) % 100}px)`
  }

  #centreIcon(smooth = false) {
    const selected = this.iconTargets.find((icon) => icon.dataset.on === "true")
    if (!selected) return

    if (smooth) {
      this.#centreInDock(selected, true)
    } else {
      requestAnimationFrame(() => this.#centreInDock(selected, false))
    }
  }

  // scrollIntoView would scroll every scrollable ancestor, and an ancestor
  // clipped with overflow-x is still scrollable programmatically — that drags
  // the whole page sideways. Scrolling the dock itself touches nothing else.
  #centreInDock(icon, smooth) {
    const dock = this.dockTarget.getBoundingClientRect()
    // A shut fold gives every rect zero width, which would scroll the dock to
    // a meaningless offset. foldToggled centres again once it has a box.
    if (!dock.width) return

    const target = icon.getBoundingClientRect()
    const delta = (target.left + target.width / 2) - (dock.left + dock.width / 2)

    this.dockTarget.scrollTo({
      left: this.dockTarget.scrollLeft + delta,
      behavior: smooth ? "smooth" : "auto"
    })
  }

  // The row keeps its space while faded, so the last figure stays put rather
  // than snapping to zero the moment the deadline is cleared.
  #renderRate() {
    const payable = this.amount > 0 && this.months > 0

    this.rateTarget.dataset.on = String(payable)

    if (payable) {
      this.rateValueTarget.textContent = this.#withSeparators(Math.round(this.amount / this.months))
    }
  }

  // What a folded question says while it is shut. The amount echoes the dial;
  // the deadline shows the term unless a date was hand-picked, in which case
  // the date itself is the more honest answer.
  #renderFoldAnswers() {
    const figure = this.#withSeparators(this.amount)
    this.foldAmountTarget.textContent =
      this.symbolFirstValue ? `${this.symbolValue}${figure}` : `${figure}${this.symbolValue}`

    this.foldDateTarget.textContent = this.#termLabel()
  }

  #termLabel() {
    if (this.months === null) return this.noDeadlineValue
    if (this.dateInputTarget.value) return this.#shortDate(this.dateInputTarget.value)

    return `${this.months} ${this.months === 1 ? this.monthUnitValue : this.monthsUnitValue}`
  }

  // The meta is a chip beside the name now, not a caption under it, so it
  // carries the date alone. "Target · " was the label of a line that no longer
  // exists, and the chip has no room to spend on it.
  #metaLabel() {
    if (this.months === null) return this.noDeadlineValue

    return this.#shortDate(this.dateFieldTarget.value)
  }

  #shortDate(value) {
    return new Date(value).toLocaleDateString(this.localeValue, { month: "short", year: "numeric" })
  }

  #dateInMonths(months) {
    const date = new Date()
    date.setMonth(date.getMonth() + months)
    return date.toISOString().slice(0, 10)
  }

  // Log scale: 100 and 100,000 both read as a level rather than empty or full.
  #fillPercent() {
    if (this.amount <= 0) return EMPTY_FILL

    return Math.min(88, 8 + Math.log10(this.amount + 1) / 5 * 80)
  }

  #withSeparators(value) {
    return value.toLocaleString(this.localeValue)
  }
}
